/**
 * THE LAUNCHER — one box that sends what you type or opens what it names
 * (RFC 0333).
 *
 * It is the real composer with the ranked rows under it. The new-pane page
 * puts it in the middle of a pane at `page` size; ⌘K puts it in an overlay at
 * `compact` size. Those used to be two components that searched the same list
 * and drew it two ways, and every change to a row had to be made twice.
 *
 * ASK IS A ROW. The first row is always "send what you typed as a session",
 * unless the text is a bare url, when Open goes above it. So what Return does
 * is always on screen: it does what the selected row says, and with nothing
 * moved that is Ask. ↓ walks into the matches; Tab opens the selected row, or
 * the first match while Ask is selected. Shift+Return breaks a line.
 *
 * It renders what it is given. What exists, and what happens when a row is
 * picked, are the shell's business (WorldShell).
 */

import type { AttachmentRef } from '@mod/core/model'
import {
  FileText,
  Globe,
  MessageSquare,
  Plus,
  SquareTerminal,
} from 'lucide-react'
import {
  type ReactNode,
  type Ref,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Composer, type ComposerProps } from '../../features/thread/Composer'
import { ComposerSlotProvider } from '../../features/thread/ComposerSlot'
import { matchWebHistory, readWebHistory } from '../../features/web/webHistory'
import { AppIcon } from '../../services/apps/AppIcon'
import { SessionMark } from '../../services/sessions/SessionMark'
import { type CommandTarget, rankTargets, toUrl } from './launcherTargets'

export type LauncherSize = 'page' | 'compact'

/** The composer props the launcher passes straight through. */
export type LauncherComposerProps = Pick<
  ComposerProps,
  | 'onAttach'
  | 'devices'
  | 'skills'
  | 'onCreateSkill'
  | 'projects'
  | 'projectId'
  | 'onSelectProject'
  | 'voice'
  | 'busy'
  | 'onStop'
>

/** The Ask row, which is not a target: it sends rather than opens. */
const ASK_ID = 'ask'
/** RFC 0336 S7: in the World view, starting a session is its own row. */
const NEW_ID = 'new-session'
/** RFC 0336: on the new-pane page, asking the World agent is the row under Ask. */
const WORLD_ID = 'ask-world'
type SendKind = 'ask' | 'new' | 'world'
type LauncherRow =
  | CommandTarget
  | { id: typeof ASK_ID; kind: 'ask'; title: string }
  | { id: typeof NEW_ID; kind: 'new'; title: string }
  | { id: typeof WORLD_ID; kind: 'world'; title: string }

/** A row that sends what is typed, rather than opening something. */
const sends = (row: LauncherRow | undefined): boolean =>
  row?.kind === 'ask' || row?.kind === 'new' || row?.kind === 'world'

/**
 * A key drawn as a key.
 *
 * Only ever rendered where the key does something AT THAT MOMENT: on the
 * selected row, the key that will act on it.
 */
function Key({ children }: { children: string }) {
  return (
    <kbd className="inline-grid h-5 min-w-[22px] place-items-center rounded-md border border-border bg-muted px-1.5 font-sans text-[11.5px] text-muted-foreground">
      {children}
    </kbd>
  )
}

/**
 * One row: an icon, a name, and the name's own metadata inline after it.
 *
 * INLINE AND MUTED rather than right-aligned. Two fields at opposite ends of a
 * row make the eye travel to assemble one fact; "Review this branch 12 files"
 * is a sentence read once, left to right.
 *
 * SHAPE SAYS WHAT A ROW DOES. A bare icon returns you to something that exists;
 * an icon in a bordered square makes something new.
 */
function ResultRow({
  row,
  size,
  selected,
  keycap,
  onPick,
  innerRef,
  world,
  worldName = 'the World agent',
}: {
  row: LauncherRow
  /** What the World agent is called (RFC 0336), for its rows. */
  worldName?: string
  size: LauncherSize
  /** The World view's wording (RFC 0336 S7): Ask goes to the World agent. */
  world?: boolean
  selected: boolean
  /** The key that acts on this row, drawn only while it is selected. */
  keycap: string
  onPick: () => void
  /** Set on the selected row only, so the keyboard can keep it in view. */
  innerRef?: Ref<HTMLButtonElement>
}) {
  const page = size === 'page'
  const creates = row.kind === 'create' || row.kind === 'new'
  const Icon =
    row.kind === 'world'
      ? Globe
      : row.kind === 'ask'
        ? world
          ? Globe
          : MessageSquare
        : row.kind === 'terminal'
          ? SquareTerminal
          : row.kind === 'web'
            ? Globe
            : FileText
  const hint =
    row.kind === 'world'
      ? 'World'
      : row.kind === 'ask'
        ? world
          ? 'World'
          : 'new session'
        : row.kind === 'new'
          ? 'pick where'
          : row.hint
  return (
    <button
      ref={innerRef}
      type="button"
      onClick={onPick}
      // `bg-muted` is #252525 in the dark theme and the card under it is
      // #232323, so the selected row was a two-per-cent difference nobody could
      // see. Selection has to read at a glance or the keyboard is unusable, so
      // it is a tint of the FOREGROUND, which separates from whatever surface
      // it lands on in either theme.
      className={`flex w-full min-w-0 items-center text-left hover:bg-foreground/[0.06] ${
        page
          ? 'gap-3.5 rounded-[10px] px-3 py-2 text-[17px]'
          : 'gap-2.5 rounded-lg px-2.5 py-1.5 text-[13.5px]'
      } ${selected ? 'bg-foreground/10 text-foreground' : 'text-foreground/90'}`}
    >
      <span
        className={`grid shrink-0 place-items-center ${page ? 'size-7' : 'size-[22px]'}`}
      >
        {creates ? (
          <span
            className={`grid size-full place-items-center border border-border text-muted-foreground ${
              page ? 'rounded-lg' : 'rounded-md'
            }`}
          >
            <Plus size={page ? 15 : 12} />
          </span>
        ) : row.kind === 'session' ? (
          // Its mark (RFC 0311), the same one the sidebar row draws.
          <SessionMark id={row.id} size={page ? 20 : 16} />
        ) : (
          <AppIcon
            appId={sends(row) ? undefined : (row as CommandTarget).appId}
            size={page ? 20 : 16}
            fallback={
              <Icon size={page ? 20 : 16} className="text-muted-foreground" />
            }
          />
        )}
      </span>
      {/* Both parts shrink, and the hint gives way FIRST: it is the disposable
          half of the sentence, and a session named by its first message can be
          a paragraph long. */}
      <span className="min-w-0 truncate">
        {row.kind === 'world'
          ? `Ask ${worldName}: ${row.title}`
          : row.kind === 'ask'
            ? world
              ? `Ask ${worldName}: ${row.title}`
              : `Ask: ${row.title}`
            : row.kind === 'new'
              ? `New session: ${row.title}`
              : row.title}
      </span>
      {hint && (
        <span
          className={`min-w-0 shrink-[999] truncate text-muted-foreground ${
            page ? 'text-[15px]' : 'text-[12px]'
          }`}
        >
          {hint}
        </span>
      )}
      {/* Always present, so the keycap sits at the right edge rather than
          against the end of a short title. */}
      <span className="flex-1" />
      {selected && (
        <span className="shrink-0">
          <Key>{keycap}</Key>
        </span>
      )}
    </button>
  )
}

export function Launcher({
  size,
  targets,
  onPickTarget,
  onAsk,
  onOpenUrl,
  composer,
  placeholder,
  autoFocus = true,
  onQueryChange,
  listClassName,
  onAskWorld,
  inside,
  chatting = false,
  onAskWorldRow,
  worldName,
  onIdleKey,
}: {
  /** `page` on the new-pane page, `compact` in the ⌘K overlay. Changes type
   *  size, row height and icon size, nothing else. */
  size: LauncherSize
  /** Everything this World can open or make: one list, built by the shell. */
  targets: CommandTarget[]
  /** Open a picked target. The shell decides where. */
  onPickTarget?: (target: CommandTarget) => void
  /**
   * The Ask row: a session, started, with that text sent.
   *
   * Carries what the composer decided ALONGSIDE the text (the model, the
   * skills named with `/`, the files attached), because those are choices
   * about this message and are lost if only the string survives.
   */
  onAsk?: (
    text: string,
    requestedModel: { provider: string; model: string },
    skills?: string[],
    attachments?: AttachmentRef[],
  ) => Promise<void> | void
  /** Open a page: a typed url, a visited page, or a web search. */
  onOpenUrl?: (url: string) => void
  /** The rest of the real composer, passed through rather than reimplemented. */
  composer?: LauncherComposerProps
  placeholder?: string
  /** Only the launcher with focus takes the caret: two side by side would
   *  otherwise race for it on every render. */
  autoFocus?: boolean
  /** Tell the host what is typed, so the page can swap its tiles for rows. */
  onQueryChange?: (query: string) => void
  /** Where the rows sit: a card on the page, a plain list in the overlay. */
  listClassName?: string
  /**
   * The World view (RFC 0336 S7). When given, the Ask row asks the World agent
   * and "New session" is the second row, where `onAsk` and the project choice
   * live. The project chips show only while that row is selected, because the
   * World agent needs no checkout.
   */
  onAskWorld?: (
    text: string,
    requestedModel: { provider: string; model: string },
    skills?: string[],
    attachments?: AttachmentRef[],
  ) => Promise<void> | void
  /**
   * Draw INSIDE the composer's own frame (RFC 0336 S7): the strip opened
   * upward, not a box over it. `header` is the line above the box, `above` sits
   * over the rows, and the rows share the frame with what you type into.
   */
  inside?: { header: ReactNode; above?: ReactNode }
  /**
   * The new-pane page (RFC 0336): a row under Ask that sends what is typed to
   * the World agent instead of starting a session. Ask stays the default.
   */
  onAskWorldRow?: (
    text: string,
    requestedModel: { provider: string; model: string },
    skills?: string[],
    attachments?: AttachmentRef[],
  ) => Promise<void> | void
  /**
   * In a conversation with the World agent (RFC 0336 S7): no rows, so what is
   * typed is the next message, not a search. Set once the World agent has
   * been asked, until the view closes.
   */
  chatting?: boolean
  /** What the World agent is called, for its rows (RFC 0336). */
  worldName?: string
  /**
   * The keys while there are no rows (RFC 0336 S10): the World view walks its
   * live lines with them. Return true when the key was used.
   */
  onIdleKey?: (key: 'up' | 'down' | 'tab' | 'enter') => boolean
}) {
  const world = onAskWorld !== undefined
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const selectedRef = useRef<HTMLButtonElement>(null)
  const typed = query.trim()
  const page = size === 'page'

  // Through a ref, so a host that passes an inline function does not reset the
  // selection on every render.
  const onQueryChangeRef = useRef(onQueryChange)
  onQueryChangeRef.current = onQueryChange
  useEffect(() => {
    onQueryChangeRef.current?.(query)
    // A new query starts from the top, where Ask (or Open) is.
    setCursor(0)
  }, [query])

  /**
   * Set while a click on the Ask row is being turned into Return.
   *
   * Clicking Ask must send through the COMPOSER, because only the composer
   * knows the model, skills and files chosen for this message. So the click
   * presses Return in the box, and this tells the key handler to let it
   * through whatever row happens to be selected.
   */
  const askingRef = useRef(false)
  /** Which sending row a click meant, read by `send` in the same tick. */
  const clickedRef = useRef<SendKind | null>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const askFromClick = (kind: SendKind = 'ask') => {
    const textarea = boxRef.current?.querySelector('textarea')
    if (!textarea) return
    askingRef.current = true
    clickedRef.current = kind
    try {
      textarea.dispatchEvent(
        new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }),
      )
    } finally {
      askingRef.current = false
      clickedRef.current = null
    }
  }

  /**
   * The rows, in the order they are drawn, which is also the keyboard's order.
   *
   * A typed url first, because nothing else in the World is spelled like one.
   * Then Ask. Then pages visited before and the records that match, a web
   * search, and last the things to make, so Tab from Ask goes to the thing you
   * named before it goes to making one.
   */
  const rows = useMemo<LauncherRow[]>(() => {
    if (!typed || chatting) return []
    const url = toUrl(typed)
    const ranked = rankTargets(targets, query)
    const open: CommandTarget[] =
      url && onOpenUrl
        ? [
            {
              id: `url:${url}`,
              title: url,
              hint: 'open a browser pane',
              kind: 'web',
            },
          ]
        : []
    const ask: LauncherRow = { id: ASK_ID, kind: 'ask', title: typed }
    const fresh: LauncherRow[] = world
      ? [{ id: NEW_ID, kind: 'new', title: typed }]
      : onAskWorldRow
        ? [{ id: WORLD_ID, kind: 'world', title: typed }]
        : []
    // Pages visited before, the way a browser's box completes from history.
    const visited: CommandTarget[] = onOpenUrl
      ? matchWebHistory(readWebHistory(), query)
          .filter((v) => v.url !== url)
          .map((v) => ({
            id: `url:${v.url}`,
            title: v.title || v.url,
            hint: v.title ? v.url.replace(/^https?:\/\//i, '') : undefined,
            kind: 'web' as const,
          }))
      : []
    // Search the web for the text, the row a browser's box always offers. Not
    // offered for a url, which opens itself.
    const search: CommandTarget[] =
      onOpenUrl && !url
        ? [
            {
              id: `url:https://www.google.com/search?q=${encodeURIComponent(typed)}`,
              title: typed,
              hint: 'search Google',
              kind: 'web',
            },
          ]
        : []
    return [
      ...open,
      ask,
      ...fresh,
      ...visited,
      ...ranked.filter((t) => t.kind !== 'create'),
      ...search,
      ...ranked.filter((t) => t.kind === 'create'),
    ]
  }, [targets, query, typed, onOpenUrl, world, chatting, onAskWorldRow])

  const active = Math.min(cursor, Math.max(0, rows.length - 1))
  const selected = rows[active]

  /**
   * Keep the selection inside the list once the list is the thing that scrolls.
   *
   * `block: 'nearest'` so a row already on screen does not move the list under
   * you: only walking off an edge scrolls, and then by one row.
   */
  const selectedId = selected?.id
  useEffect(() => {
    if (!selectedId) return
    // Feature-detected, not assumed: jsdom has no layout and no scrollIntoView.
    selectedRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [selectedId])

  /** The composer's own send is the Ask row: it carries the model and files.
   *  In the World view it asks the World agent, unless New session is the row
   *  being acted on, which starts one where the chips say. */
  const send: ComposerProps['onSend'] = async (
    text,
    requestedModel,
    skills,
    attachments,
  ) => {
    const kind = clickedRef.current ?? selected?.kind
    if (kind === 'world' && onAskWorldRow) {
      await onAskWorldRow(text, requestedModel, skills, attachments)
      return
    }
    if (world && kind !== 'new') {
      await onAskWorld?.(text, requestedModel, skills, attachments)
      return
    }
    await onAsk?.(text, requestedModel, skills, attachments)
  }

  const pick = (row: LauncherRow) => {
    if (sends(row)) return
    if (row.kind === 'web') {
      // The id carries the url: a history row's title is the page's name.
      onOpenUrl?.(row.id.slice('url:'.length))
      return
    }
    onPickTarget?.(row as CommandTarget)
  }

  /** The first row that opens something, which is what Tab takes from Ask. */
  const firstMatch = rows.find((r) => !sends(r))

  /**
   * The keys the LIST needs, handed over by the composer.
   *
   * Returning false gives the key back: with nothing to select, ↓ must still
   * move the caret and Tab must still leave the box, and Return on Ask is the
   * composer's own send, so the model and files it chose go with the text.
   */
  const onNavigationKey = (key: 'up' | 'down' | 'tab' | 'enter'): boolean => {
    if (askingRef.current) return false
    if (rows.length === 0) return onIdleKey?.(key) ?? false
    // The caret first while there is a line to move to: in a multi-line
    // draft, ↓ means "next line", and taking it for the rows would make the
    // box uneditable past line one. By TEXT line, not wrapped line, so a long
    // soft-wrapped line hands ↓ to the rows one press early, never a draft.
    const box = boxRef.current?.querySelector('textarea')
    if (
      key === 'down' &&
      box &&
      box.value.slice(box.selectionEnd ?? 0).includes('\n')
    )
      return false
    if (
      key === 'up' &&
      box &&
      box.value.slice(0, box.selectionStart ?? 0).includes('\n')
    )
      return false
    if (key === 'down') {
      setCursor(Math.min(active + 1, rows.length - 1))
      return true
    }
    if (key === 'up') {
      setCursor(Math.max(active - 1, 0))
      return true
    }
    if (!selected) return false
    if (key === 'enter') {
      if (sends(selected)) return false
      pick(selected)
      return true
    }
    const target = sends(selected) ? firstMatch : selected
    if (!target) return false
    pick(target)
    return true
  }

  const list =
    rows.length > 0 ? (
      // THE LIST SCROLLS, NOT THE PAGE, so the box you are typing into never
      // leaves the screen as the matches grow.
      <div className={listClassName}>
        {rows.map((row) => {
          const isSelected = row.id === selected?.id
          return (
            <ResultRow
              key={row.id}
              row={row}
              size={size}
              selected={isSelected}
              keycap="↵"
              innerRef={isSelected ? selectedRef : undefined}
              world={world}
              {...(worldName ? { worldName } : {})}
              onPick={() =>
                row.kind === 'ask' || row.kind === 'new' || row.kind === 'world'
                  ? askFromClick(row.kind)
                  : pick(row)
              }
            />
          )
        })}
      </div>
    ) : null

  return (
    <>
      {/* THE REAL COMPOSER, not a lookalike: model and harness, attachments,
          `/` skills and the project choice are here because it is the same
          component, and stay here as that component grows. `page` scale on the
          new-pane page, the thread's `panel` scale in ⌘K, the strip's
          `compact` when drawn inside it. Its own slot provider, so wrapping it
          changes this composer and no other. */}
      <div ref={boxRef} className="contents">
        <ComposerSlotProvider
          value={{
            variant: inside ? 'compact' : page ? 'page' : 'panel',
            element: null,
          }}
        >
          <Composer
            {...composer}
            onSend={send}
            onDraftChange={setQuery}
            onNavigationKey={onNavigationKey}
            placeholder={placeholder}
            autoFocus={autoFocus}
            // Nothing has been said in a session that does not exist yet, so the
            // project choice is open here: always, except in the World view,
            // where it shows only for New session (RFC 0336 S7).
            // Nor for the World agent's row, which needs no checkout.
            unstarted={
              (!world && selected?.kind !== 'world') || selected?.kind === 'new'
            }
            {...(inside
              ? {
                  holdings: inside.header,
                  activity:
                    inside.above || list ? (
                      <>
                        {list ? null : inside.above}
                        {list}
                      </>
                    ) : undefined,
                }
              : {})}
          />
        </ComposerSlotProvider>
      </div>

      {inside ? null : list}
    </>
  )
}
